import { useEffect, useState } from "react";
import axios from "axios";
import { X, Megaphone, AlertTriangle, AlertCircle, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VARIANT_STYLE = {
  informativo: "bg-slate-900 text-white",
  importante:  "bg-amber-500 text-slate-900",
  urgente:     "bg-red-600 text-white",
};
const VARIANT_ICON = {
  informativo: Info,
  importante: AlertTriangle,
  urgente: AlertCircle,
};

const DISMISS_KEY = "ascauto:dismissed_announcements";

function isDismissed(id) {
  try { return (JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")).includes(id); }
  catch { return false; }
}
function markDismissed(id) {
  try {
    const arr = JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]");
    if (!arr.includes(id)) arr.push(id);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(arr));
  } catch {}
}

/**
 * AnnouncementBar — Iter 207
 * Muestra los avisos web públicos según su posición.
 *
 * Props:
 *   position: "top_bar" | "drawer" | "home_slide" | "booking_form"
 */
export default function AnnouncementBar({ position = "top_bar" }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/web-announcements/public`)
      .then(r => { if (!cancel) setItems(r.data?.items || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const visible = items.filter(a =>
    a.position === position &&
    a.active &&
    !isDismissed(a.id)
  );

  if (visible.length === 0) return null;

  return (
    <div className="w-full" data-testid={`announcement-bar-${position}`}>
      {visible.map((a) => {
        const Icon = VARIANT_ICON[a.variant] || Megaphone;
        const style = VARIANT_STYLE[a.variant] || VARIANT_STYLE.informativo;
        const isExpanded = !!expanded[a.id];
        const hideMobile = !a.show_on_mobile ? "hidden md:block" : "";
        return (
          <div key={a.id}
               className={`${style} ${hideMobile} relative`}
               data-testid={`ann-${a.id}`}>
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-start justify-between gap-3 text-sm">
              <div className="flex items-start gap-2 flex-1">
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="leading-snug" data-testid={`ann-short-${a.id}`}>{a.short_text}</p>
                  {a.long_text && (
                    <>
                      {isExpanded && (
                        <p className="mt-2 text-[13px] opacity-90 leading-relaxed" data-testid={`ann-long-${a.id}`}>{a.long_text}</p>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpanded(s => ({ ...s, [a.id]: !s[a.id] }))}
                        className="mt-1 text-xs underline opacity-80 hover:opacity-100"
                        data-testid={`ann-toggle-${a.id}`}
                      >
                        {isExpanded ? "Ver menos" : "Ver más"}
                      </button>
                    </>
                  )}
                  {(a.button1_text || a.button2_text) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {a.button1_text && a.button1_url && (
                        <a href={a.button1_url}
                           className="inline-flex items-center px-3 py-1 rounded-sm text-xs font-semibold bg-white/20 hover:bg-white/30 border border-white/40"
                           data-testid={`ann-btn1-${a.id}`}>
                          {a.button1_text}
                        </a>
                      )}
                      {a.button2_text && a.button2_url && (
                        <a href={a.button2_url}
                           target={a.button2_url.startsWith("http") ? "_blank" : undefined}
                           rel={a.button2_url.startsWith("http") ? "noopener noreferrer" : undefined}
                           className="inline-flex items-center px-3 py-1 rounded-sm text-xs font-semibold bg-white/20 hover:bg-white/30 border border-white/40"
                           data-testid={`ann-btn2-${a.id}`}>
                          {a.button2_text}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {a.dismissible && (
                <button type="button"
                        onClick={() => { markDismissed(a.id); setItems(s => s.filter(x => x.id !== a.id)); }}
                        className="opacity-70 hover:opacity-100 flex-shrink-0"
                        aria-label="Cerrar aviso"
                        data-testid={`ann-close-${a.id}`}>
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
